// Standard Arabic alphabet — 28 letters
// English keys map to closest phonetic Arabic equivalent.

export const arabicLetters = [
  { arabic: 'ا', english: 'A', name: 'Alif' },
  { arabic: 'ب', english: 'B', name: 'Ba' },
  { arabic: 'ت', english: 'T', name: 'Ta' },
  { arabic: 'ث', english: 'C', name: 'Tha' },
  { arabic: 'ج', english: 'J', name: 'Jeem' },
  { arabic: 'ح', english: 'H', name: 'Hha' },
  { arabic: 'خ', english: 'K', name: 'Kha' },
  { arabic: 'د', english: 'D', name: 'Dal' },
  { arabic: 'ذ', english: 'Z', name: 'Dhal' },
  { arabic: 'ر', english: 'R', name: 'Ra' },
  { arabic: 'ز', english: 'X', name: 'Zay' },
  { arabic: 'س', english: 'S', name: 'Seen' },
  { arabic: 'ش', english: 'U', name: 'Sheen' },
  { arabic: 'ص', english: 'E', name: 'Sad' },
  { arabic: 'ض', english: 'Q', name: 'Dad' },
  { arabic: 'ط', english: 'I', name: 'Tah' },
  { arabic: 'ظ', english: 'O', name: 'Zah' },
  { arabic: 'ع', english: 'G', name: 'Ayn' },
  { arabic: 'غ', english: 'P', name: 'Ghayn' },
  { arabic: 'ف', english: 'F', name: 'Fa' },
  { arabic: 'ق', english: 'V', name: 'Qaf' },
  { arabic: 'ك', english: 'L', name: 'Kaf' },
  { arabic: 'ل', english: 'M', name: 'Lam' },
  { arabic: 'م', english: 'N', name: 'Meem' },
  { arabic: 'ن', english: 'Y', name: 'Noon' },
  { arabic: 'ه', english: 'W', name: 'Ha' },
  { arabic: 'و', english: 'Wo', name: 'Waw' },
  { arabic: 'ي', english: 'YE', name: 'Ya' },
];

// Common additional forms
export const arabicAdditionalForms = [
  { arabic: 'ة', english: 'h', name: 'Ta Marbuta' },
  { arabic: 'ى', english: 'a', name: 'Alif Maqsura' },
  { arabic: 'ء', english: "'", name: 'Hamza' },
  { arabic: 'أ', english: 'A2', name: 'Alif w/ Hamza' },
  { arabic: 'إ', english: 'I2', name: 'Alif w/ Hamza Below' },
  { arabic: 'آ', english: 'AA', name: 'Alif Madda' },
  { arabic: 'ؤ', english: 'W2', name: 'Waw w/ Hamza' },
  { arabic: 'ئ', english: 'Y2', name: 'Ya w/ Hamza' },
  { arabic: 'لا', english: 'LA', name: 'Lam-Alif' },
];

// Common Islamic / memorial abbreviations & phrases used on tombstones
export const arabicAbbreviations = [
  { arabic: 'بسم الله الرحمن الرحيم', english: 'Bism', name: 'In the name of God' },
  { arabic: 'إنا لله وإنا إليه راجعون', english: 'Inna', name: 'To God we return' },
  { arabic: 'رحمه الله', english: 'RA-M', name: 'May God have mercy on him' },
  { arabic: 'رحمها الله', english: 'RA-F', name: 'May God have mercy on her' },
  { arabic: 'المرحوم', english: 'M-M', name: 'The deceased (m)' },
  { arabic: 'المرحومة', english: 'M-F', name: 'The deceased (f)' },
];

export const arabicSymbols = [
  { arabic: ' ', english: ' ', name: 'Space' },
  { arabic: '،', english: ',', name: 'Arabic Comma' },
  { arabic: '.', english: '.', name: 'Period' },
  { arabic: '؛', english: ';', name: 'Arabic Semicolon' },
  { arabic: '؟', english: '?', name: 'Arabic Question' },
  { arabic: '"', english: '"', name: 'Quote' },
  { arabic: '-', english: '-', name: 'Dash' },
  { arabic: '٠', english: '0', name: 'Arabic Zero' },
  { arabic: '١', english: '1', name: 'Arabic One' },
  { arabic: '٢', english: '2', name: 'Arabic Two' },
  { arabic: '٣', english: '3', name: 'Arabic Three' },
  { arabic: '٤', english: '4', name: 'Arabic Four' },
  { arabic: '٥', english: '5', name: 'Arabic Five' },
  { arabic: '٦', english: '6', name: 'Arabic Six' },
  { arabic: '٧', english: '7', name: 'Arabic Seven' },
  { arabic: '٨', english: '8', name: 'Arabic Eight' },
  { arabic: '٩', english: '9', name: 'Arabic Nine' },
];

// English -> Arabic mapping for the auto-convert editor.
// Multi-character keys (e.g. AA, RA-M) cannot be auto-converted from typing;
// those are inserted by clicking buttons in the keyboard panel.
const buildMap = () => {
  const map = {};
  arabicLetters.forEach((item) => {
    if (item.english.length === 1) {
      map[item.english.toLowerCase()] = item.arabic;
      map[item.english.toUpperCase()] = item.arabic;
    }
  });
  arabicAdditionalForms.forEach((item) => {
    if (item.english.length === 1) {
      map[item.english] = item.arabic;
    }
  });
  arabicSymbols.forEach((item) => {
    map[item.english] = item.arabic;
  });
  return map;
};

export const englishToArabic = buildMap();

export const convertToArabic = (text) =>
  text
    .split('')
    .map((char) => englishToArabic[char] || char)
    .join('');

// For the proof sheet reference chart: split letters into two rows like the Hebrew chart
export const arabicChartRow1 = arabicLetters.slice(0, 14);
export const arabicChartRow2 = arabicLetters.slice(14, 28);
